import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ProjectAction from "../actions/ProjectAction";
import { isJiraContext } from "../api/api";
import { fromJS } from "immutable";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import LayoutAction from "../actions/LayoutAction";
import MemberInvitationDialog from "../dialog/MemberInvitationDialog";
import useUpgradePlan from "./hooks/useUpgradePlan";
import GridTable from "../grid-v2/GridTable";
import { ClipboardIcon, UserPlus } from "lucide-react";
import InfoDialog from "../dialog/InfoDialog";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import { SelectField } from "../components/ui";
import TenantAction from "../actions/TenantAction";
import { generatePassword } from "../api/AppLinkApi";
import JiraUserPicker, { JiraUserOption } from "../components/JiraUserPicker";
import { addJiraUsersToTeam } from "../api/TenantApi";

const MemberSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();
  const { invalidMaxUsersPerTenant } = useUpgradePlan();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const accountId = currentUser.get("accountId");
  const isAdmin = currentUser.get("admin");
  const isSysAdmin = currentUser.get("sysAdmin");
  const teamMembers = useSelector((state: any) => state.tenant.get("teamMembers")) || fromJS([]);

  const [showMemberInvitation, setShowMemberInvitation] = useState(false);
  const [selectedJiraUsers, setSelectedJiraUsers] = useState<JiraUserOption[]>([]);
  const [isAddingJiraUsers, setIsAddingJiraUsers] = useState(false);

  const [roleList, setRoleList] = useState<any[]>([]);
  const [role, setRole] = useState<boolean>(undefined);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [status, setStatus] = useState<boolean>(undefined);

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);

  const [showInfo, setShowInfo] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const memberHeaders = [
    { key: "user", title: "user" },
    { key: "role", title: "role" },
    { key: "status", title: "status" },
    { key: "action", title: "" },
  ];

  useEffect(() => {
    dispatch(TenantAction.getTeamMembers());
  }, [dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const getStatus = (active: boolean) => {
    return (
      <div
        className={`capitalize w-max rounded-full border border-gray-200 py-1 px-2 ${
          active ? "text-white bg-blue-600" : "text-white bg-red-500"
        }`}
      >
        {active ? t("Active") : t("Inactive")}
      </div>
    );
  };

  useEffect(() => {
    const roleDatas = [];
    const statusDatas = [];
    teamMembers.toJS().map((item) => {
      if (!roleDatas.some((obj) => obj.value === item.admin)) {
        roleDatas.push({
          value: item.admin,
          label: item.admin ? t("Admin") : t("User"),
        });
      }
      if (!statusDatas.some((obj) => obj.value === item.active)) {
        statusDatas.push({
          value: item.active,
          label: getStatus(item.active),
        });
      }
    });
    setRoleList(roleDatas);
    setStatusList(statusDatas);
  }, [teamMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickInviteMember = () => {
    if (invalidMaxUsersPerTenant(teamMembers.size)) return;
    setShowMemberInvitation(true);
  };

  const handleAddJiraUsers = async (users: JiraUserOption[]) => {
    if (users.length === 0) return;

    // Check user limit before adding
    if (invalidMaxUsersPerTenant(teamMembers.size + users.length)) {
      return;
    }

    setIsAddingJiraUsers(true);
    try {
      const accountIds = users.map((user) => user.accountId);
      await addJiraUsersToTeam(accountIds);

      // Refresh team members list
      dispatch(TenantAction.getTeamMembers());
      dispatch(ProjectAction.getProject(projectId));

      // Clear selection
      setSelectedJiraUsers([]);

      dispatch(
        LayoutAction.setNotification({
          title: t("Success"),
          type: "success",
          message: t("Users added successfully"),
        })
      );
    } catch {
      dispatch(
        LayoutAction.setNotification({
          title: t("Error"),
          type: "error",
          message: t("Failed to add users"),
        })
      );
    } finally {
      setIsAddingJiraUsers(false);
    }
  };

  const handleGeneratePassword = (userId) => {
    setShowConfirm({
      show: true,
      title: t("Generate Password"),
      description: t("Are you sure you want to generate a new password for this member?"),
      type: "GENERATE_PASSWORD",
      data: userId,
    });
  };

  const handleEnableAdmin = (userId, admin) => {
    setShowConfirm({
      show: true,
      title: admin ? t("Make User") : t("Make Admin"),
      description: t("Are you sure you want to make {{text}} this member?", {
        text: admin ? t("user") : t("admin"),
      }),
      type: "ENABLE_ADMIN",
      data: userId,
    });
  };

  const handleActiveUser = (userId, active) => {
    setShowConfirm({
      show: true,
      title: active ? t("Deactivate Member") : t("Activate Member"),
      description: t("Are you sure you want to {{text}} this member?", {
        text: active ? t("deactivate") : t("activate"),
      }),
      type: "ACTIVE_USER",
      data: userId,
    });
  };

  const handleRemoveUser = (userId) => {
    setShowConfirm({
      show: true,
      title: t("Remove Member"),
      description: t("Are you sure you want to remove this member?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "REMOVE_USER",
      data: userId,
    });
  };

  const filterData = useMemo(() => {
    let datas = teamMembers
      .toJS()
      .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    if (debouncedSearchText) {
      datas = datas.filter(
        (item) =>
          (item.displayName &&
            item.displayName
              .trim()
              .toLowerCase()
              .includes(debouncedSearchText.trim().toLowerCase())) ||
          (item.email &&
            item.email.trim().toLowerCase().includes(debouncedSearchText.trim().toLowerCase()))
      );
    }
    if (role !== null && role !== undefined) {
      datas = datas.filter((item) => item.admin === role);
    }
    if (status !== null && status !== undefined) {
      datas = datas.filter((item) => item.active === status);
    }
    return datas.map((item) => {
      return {
        user: (
          <div className="flex items-center min-w-0 gap-x-2">
            <img
              className="h-8 w-8 flex-none rounded-full bg-gray-50"
              src={item.avatarUrl}
              alt=""
            />
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-gray-900">
                {item.displayName}
                {item.email ? ` (${item.email})` : ""}
              </p>
            </div>
          </div>
        ),
        role: item.admin ? t("Admin") : t("User"),
        status: getStatus(item.active),
        action: isAdmin ? (
          <Menu as="div" className="relative flex-none w-max">
            <MenuButton className="block p-2.5 text-gray-500 hover:text-gray-900">
              <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
            </MenuButton>
            <MenuItems
              transition
              anchor="bottom start"
              className="absolute right-0 z-10 mt-2 w-max origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
            >
              {isSysAdmin ? (
                <MenuItem>
                  <div
                    onClick={() => {
                      handleGeneratePassword(item.accountId);
                    }}
                    className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                  >
                    {t("Generate Password")}
                  </div>
                </MenuItem>
              ) : null}
              {item.accountId !== accountId ? (
                <>
                  <MenuItem>
                    <div
                      onClick={() => {
                        handleActiveUser(item.accountId, item.active);
                      }}
                      className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                    >
                      {item.active ? t("Deactivate") : t("Activate")}
                    </div>
                  </MenuItem>
                  <MenuItem>
                    <div
                      onClick={() => {
                        handleEnableAdmin(item.accountId, item.admin);
                      }}
                      className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                    >
                      {item.admin ? t("Make User") : t("Make Admin")}
                    </div>
                  </MenuItem>
                  <MenuItem>
                    <div
                      onClick={() => {
                        handleRemoveUser(item.accountId);
                      }}
                      className="block px-3 py-1 text-sm/6 text-red-600 data-focus:bg-gray-50 data-focus:outline-hidden"
                    >
                      {t("Remove")}
                    </div>
                  </MenuItem>
                </>
              ) : null}
            </MenuItems>
          </Menu>
        ) : null,
      };
    });
  }, [teamMembers, debouncedSearchText, role, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (showConfirm.type === "GENERATE_PASSWORD") {
      generatePassword(showConfirm.data).then((data) => {
        setShowInfo({
          show: true,
          title: t("Generate Password"),
          description: data?.generatedPassword ? (
            <div>
              <div>
                {t("The new password for email")} <span className="font-bold">{data?.email}</span>
              </div>
              <div className="flex items-center justify-center">
                <div className="mr-2 font-bold">{data?.generatedPassword}</div>
                {data?.generatedPassword ? (
                  <button
                    onClick={() => {
                      if (!data?.generatedPassword) return;
                      navigator.clipboard?.writeText(data?.generatedPassword);
                      dispatch(
                        LayoutAction.setNotification({
                          title: t("Copied to clipboard"),
                          type: "success",
                          message: t("Password copied to clipboard"),
                        })
                      );
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    title="Copy"
                  >
                    <ClipboardIcon className="w-5 h-5 text-gray-500" />
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="font-semibold">{t("Password generation failed")}</div>
          ),
        });
      });
    } else if (showConfirm.type === "ENABLE_ADMIN") {
      dispatch(TenantAction.enableAdminTeamMember(showConfirm.data)).then(() => {
        dispatch(ProjectAction.getProject(projectId));
        dispatch(TenantAction.getTeamMembers());
      });
    } else if (showConfirm.type === "ACTIVE_USER") {
      dispatch(TenantAction.activeTeamMember(showConfirm.data)).then(() => {
        dispatch(ProjectAction.getProject(projectId));
        dispatch(TenantAction.getTeamMembers());
      });
    } else if (showConfirm.type === "REMOVE_USER") {
      dispatch(TenantAction.removeTeamMember(showConfirm.data)).then(() => {
        dispatch(ProjectAction.getProject(projectId));
        dispatch(TenantAction.getTeamMembers());
      });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const showInvitation = isAdmin && !isJiraContext();
  const showJiraUserPicker = isAdmin && isJiraContext();

  // Get existing account IDs to exclude from picker
  const existingAccountIds = teamMembers.toJS().map((member: any) => member.accountId);

  return (
    <div className="py-4 max-w-5xl">
      <div className="py-2 px-4 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("Members")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("Manage team members, set access levels")}
        </p>
      </div>
      <div className="py-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <div className="relative w-full mr-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder={t("Find member")}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-600 text-sm font-medium placeholder-gray-500"
                onChange={(e) => {
                  const newVal = e.target.value || "";
                  setSearchText(newVal);
                }}
              />
            </div>
            <SelectField
              className="w-50 mr-2"
              value={roleList.find((option) => option.value === role)}
              onChange={(selectedOption) => setRole(selectedOption?.value)}
              options={roleList}
              placeholder={t("Role")}
              isClearable
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
            />
            <SelectField
              className="w-50"
              value={statusList.find((option) => option.value === status)}
              onChange={(selectedOption) => setStatus(selectedOption?.value)}
              options={statusList}
              placeholder={t("Status")}
              isClearable
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
            />
          </div>
          <div>
            {showInvitation && (
              <button
                onClick={handleClickInviteMember}
                className="flex items-center gap-2 text-white bg-blue-600 rounded-lg px-3 py-2 font-medium cursor-pointer"
              >
                <UserPlus className="w-4 h-4" />
                {t("Invite Member")}
              </button>
            )}
          </div>
        </div>
        {showJiraUserPicker && (
          <div className="mb-4 flex items-center gap-2">
            <div className="flex-1">
              <JiraUserPicker
                value={selectedJiraUsers}
                onChange={setSelectedJiraUsers}
                placeholder={t("Search and add Jira users...")}
                excludeAccountIds={existingAccountIds}
                isMulti={true}
              />
            </div>
            <button
              onClick={() => handleAddJiraUsers(selectedJiraUsers)}
              disabled={selectedJiraUsers.length === 0 || isAddingJiraUsers}
              className="flex items-center gap-2 text-white bg-blue-600 rounded-lg px-4 py-2.5 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingJiraUsers ? (
                <>
                  <span className="animate-spin">⏳</span>
                  {t("Adding...")}
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  {t("Add Users")}
                </>
              )}
            </button>
          </div>
        )}
        <GridTable headers={memberHeaders} rows={filterData} pageSize={10} />
      </div>
      <MemberInvitationDialog
        visible={showMemberInvitation}
        onClose={() => {
          setShowMemberInvitation(false);
        }}
        onSubmit={() => {
          setShowMemberInvitation(false);
          dispatch(
            LayoutAction.setNotification({
              title: t("Invitation"),
              type: "success",
              message: t("Invitation sent successfully"),
            })
          );
        }}
        projectId={projectId}
        scope="TEAM_INVITATION"
      />
      <InfoDialog
        visible={showInfo.show}
        title={showInfo.title}
        description={showInfo.description}
        onClose={() => {
          setShowInfo({ show: false, title: "", description: "" });
        }}
      />
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default MemberSetting;
